import { useState } from 'react'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import { Loader2, TriangleAlert } from 'lucide-react'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '@ludus/ui'
import type { Manager, ManagerId, Pipeline } from '@ludus/core'
import { describeMerge, type ManagerMergePreview } from '@/lib/api/managers.ts'
import type { PipelineApiError } from '@/lib/api/pipelines.ts'
import { fullName } from '../utils/managerName.ts'
import { saveErrorKey } from './saveError.ts'

interface MergeManagerDialogProps {
  manager: Manager
  others: Manager[]
  pipelines: Pipeline[]
  onOpenChange: (next: boolean) => void
  onMerge: (absorbedId: ManagerId) => Promise<unknown>
}

export function MergeManagerDialog({
  manager,
  others,
  pipelines,
  onOpenChange,
  onMerge,
}: MergeManagerDialogProps) {
  const { t } = useTranslation()
  const [absorbed, setAbsorbed] = useState<Manager | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<PipelineApiError | null>(null)
  const preview = useSWR<ManagerMergePreview, PipelineApiError>(
    absorbed && `/managers/${manager.id}/merge/${absorbed.id}`,
    absorbed && (() => describeMerge(manager.id, absorbed.id)),
  )
  const canMerge =
    absorbed !== null && preview.data !== undefined && preview.data.plan.conflicts.length === 0

  const merge = async () => {
    if (busy || !canMerge) return
    setBusy(true)
    setError(null)
    try {
      await onMerge(absorbed.id)
      onOpenChange(false)
    } catch (cause) {
      setError(cause as PipelineApiError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.managers.merge.title', { name: fullName(manager) })}
            </DialogTitle>
            <DialogDescription>{t('pages.managers.merge.description')}</DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="flex flex-col gap-5 py-5">
          <label className="flex flex-col gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
              {t('pages.managers.merge.pick')}
            </span>
            <Select
              value={absorbed?.id ?? ''}
              onValueChange={(next) => setAbsorbed(others.find((m) => m.id === next) ?? null)}
              placeholder={t('pages.managers.merge.pickPlaceholder')}
              options={others.map((m) => ({
                value: m.id,
                label: fullName(m),
                hint: m.isActive ? undefined : t('pages.managers.inactive'),
              }))}
              className="w-full"
              triggerClassName="w-full justify-between"
              disabled={busy}
            />
          </label>
          {absorbed !== null && preview.data !== undefined && (
            <MergePreview absorbed={absorbed} preview={preview.data} pipelines={pipelines} />
          )}
          {preview.isLoading && (
            <p className="flex items-center gap-2 text-xs text-muted" aria-live="polite">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              {t('pages.managers.merge.loading')}
            </p>
          )}
          {preview.error && (
            <p role="alert" className="text-[11px] font-medium text-toast-error-text">
              {t('pages.managers.merge.previewFailed')}
            </p>
          )}
          {error && (
            <p role="alert" className="text-[11px] font-medium text-toast-error-text">
              {t(saveErrorKey(error))}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('pages.managers.name.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={merge}
            disabled={busy || !canMerge}
          >
            {t('pages.managers.merge.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MergePreview({
  absorbed,
  preview,
  pipelines,
}: {
  absorbed: Manager
  preview: ManagerMergePreview
  pipelines: Pipeline[]
}) {
  const { t } = useTranslation()
  const { plan } = preview
  if (plan.conflicts.length > 0) {
    return (
      <p
        role="alert"
        className="flex gap-2.5 rounded-md border border-toast-warning-text/30 bg-toast-warning px-3 py-2.5 text-sm text-toast-warning-text"
      >
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t('pages.managers.merge.conflict', { sources: plan.conflicts.join(', ') })}</span>
      </p>
    )
  }

  const moves: string[] = []
  if (plan.identities.length > 0) {
    moves.push(
      t('pages.managers.merge.moves.identities', {
        list: plan.identities.map((i) => `${i.adapterKind} · ${i.externalRef}`).join(', '),
      }),
    )
  }
  if (plan.pipelinesAdded.length > 0) {
    const boards = plan.pipelinesAdded.map((id) => pipelines.find((p) => p.id === id)?.name ?? id)
    moves.push(t('pages.managers.merge.moves.boards', { list: boards.join(', ') }))
  }
  if (plan.extensionsAdded.length > 0) {
    moves.push(
      t('pages.managers.merge.moves.extensions', { list: plan.extensionsAdded.join(', ') }),
    )
  }
  moves.push(t('pages.managers.merge.moves.cards', { count: preview.cards }))
  if (preview.schedules > 0) moves.push(t('pages.managers.merge.moves.schedule'))

  const stays = [t('pages.managers.merge.stays.name', { name: fullName(absorbed) })]
  if (plan.frozen.phone !== null) {
    stays.push(t('pages.managers.merge.stays.phone', { phone: plan.frozen.phone }))
  }
  if (plan.frozen.email !== null) {
    stays.push(t('pages.managers.merge.stays.email', { email: plan.frozen.email }))
  }

  return (
    <div className="flex flex-col gap-4 text-sm text-foreground">
      <Consequences title={t('pages.managers.merge.moves.title')} lines={moves} />
      <Consequences title={t('pages.managers.merge.stays.title')} lines={stays} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted">{t('pages.managers.merge.after')}</span>
        <Badge tone={plan.kept.isActive ? 'success' : 'warning'} size="sm">
          {t(plan.kept.isActive ? 'pages.managers.merge.active' : 'pages.managers.inactive')}
        </Badge>
      </div>
      <p className="text-xs font-medium text-toast-error-text">
        {t('pages.managers.merge.irreversible')}
      </p>
    </div>
  )
}

function Consequences({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
        {title}
      </span>
      <ul className="flex flex-col gap-1.5">
        {lines.map((line) => (
          <li key={line} className="flex gap-2.5">
            <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
